<script setup lang="ts">
import { useTemplateRef, watch, computed, ref, onMounted } from "vue"
import Card from "primevue/card"
import Divider from "primevue/divider"
import AlertsTable from "@/components/alerts/AlertsTable.vue"
import ToggleButton from "primevue/togglebutton"
import AlertsFilters from "@/components/alerts/AlertsFilters.vue"
import InputPopover from "@/components/InputPopover.vue"
import Button from "primevue/button"
// import SrInputNumber from "@/components/SrInputNumber.vue"
// import IftaLabel from "primevue/iftalabel"
import { useSwapAlertsStore } from "@/stores/swapAlertsStore"
import { useRoute } from "vue-router"
import { keysToSnake, getBuyMarketType, getSellMarketType, parseCommaSeparatedAssets } from "@/utils"
import type { MarketType } from "@/utils/types"
import type { FormSubmitEvent } from "@primevue/forms"
import { getSwapTableColumns } from "@/columns/swapColumns"
import { getSpotSwapTableColumns } from "@/columns/spotSwapColumns"
import { getSpotSpotTableColumns } from "@/columns/spotSpotColumns"
import { getSwapSpotTableColumns } from "@/columns/swapSpotColumns"
import { useAuthStore } from "@/stores/authStore"
import { useThemeStore } from "@/stores/themeStore"
import { useTelegramSignals } from "@/composable/useTgSignals"
import { useSwapFilters } from "@/composable/filters/useSwapFilters"
import { useSwapCellFormatter } from "@/composable/formatters/useSwapCellFormatter"
// import { useChangeAmount } from "@/composable/useChangeAmount"
import { useAlertsWs } from "@/composable/useAlertsWs"
import { useSaveFilters } from "@/composable/useSaveFilters"
import MarketSpreadAlertsModal from "@/components/modals/MarketSpreadAlertsModal.vue"
import SrGroupDataTable from "@/components/tables/SrGroupDataTable.vue"
import type { SwapAlertsFilter, SwapAlertSortingKey } from '@/services/alerts-v2-service/types'

// Сторы и композаблы
const route = useRoute();
const authStore = useAuthStore()
const alertsStore = useSwapAlertsStore()
const themeStore = useThemeStore()
const { formattingRules } = useSwapCellFormatter()

const marketType = computed(() => (route.meta.marketType ?? 'swap') as MarketType)
const isSpot = computed(() => getBuyMarketType(marketType.value) === 'spot')
const isSpotSell = computed(() => getSellMarketType(marketType.value) === 'spot')

const tgKeyMap: Record<string, string> = {
  swap: 'enable_alerts_ff',
  'spot-swap': 'enable_alerts_sf',
  'spot': 'enable_alerts_ss',
  'swap-spot': 'enable_alerts_fs',
}
const tgKey = computed(() => tgKeyMap[marketType.value] as 'enable_alerts_ff' | 'enable_alerts_sf' | 'enable_alerts_ss' | 'enable_alerts_fs')
const { tgSignals } = useTelegramSignals(tgKey.value)
// const { AMOUNT_MIN, AMOUNT_MAX, amountUsdt, updateAmount } = useChangeAmount(marketType)
const { saveSwapFilters } = useSaveFilters()

const getFilterPrefix = (mt: MarketType): string => {
  switch (mt) {
    case 'spot-swap': return 'spot_futures_'
    case 'spot': return 'spot_spot_'
    case 'swap-spot': return 'futures_spot_'
    default: return 'futures_futures_'
  }
}

// WebSocket для real-time alerts
const { start: startWs } = useAlertsWs(
  computed(() => alertsStore.filters as SwapAlertsFilter),
  computed(() => alertsStore.sortBy as SwapAlertSortingKey),
  computed(() => alertsStore.sortOrder == -1 ? 'desc' : 'asc'),
  computed(() => alertsStore.page ?? 0),
  (data) => alertsStore.updateFromWs(data),
)

// UI-элементы
const alertsTable = useTemplateRef('alertsTable')
const marketSpreadAlertsModal = useTemplateRef('marketSpreadAlertsModal')
const gridClasses = computed(() => {
  if (isSpot.value && isSpotSell.value) {
    return 'gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-8'
  } else if (!isSpot.value && isSpotSell.value) {
    return 'gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-5'
  }
  else if (isSpot.value || isSpotSell.value) {
    return 'gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-8'
  }
  return 'gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
})
const { formData, zodForm, loadFiltersFromSettings, filters } = useSwapFilters(alertsStore, marketType.value)

// Таблица
const tableColumns = computed(() => {
  switch (marketType.value) {
    case 'spot-swap':
      return getSpotSwapTableColumns()
    case 'spot':
      return getSpotSpotTableColumns()
    case 'swap-spot':
      return getSwapSpotTableColumns()
    default:
      return getSwapTableColumns()
  }
})

const titleKey = computed(() => {
  switch (marketType.value) {
    case 'spot-swap':
      return 'spotSwapArb'
    case 'spot':
      return 'spotSpotArb'
    case 'swap-spot':
      return 'swapSpotArb'
    default:
      return 'swapArb'
  }
})

const tgBotLink = computed(() => {
  switch (marketType.value) {
    case 'spot-swap':
      return 'https://t.me/spreadracer_spot_futures_bot'
    case 'spot':
      return 'https://t.me/spreadracer_spot_bot'
    case 'swap-spot':
      return 'https://t.me/spreadracer_futures_spot_bot'
    default:
      return 'https://t.me/spreadracer_futures_bot'
  }
})

// Прочие переменные
const pageInited = ref(false)

onMounted(async () => {
  alertsStore.clear()
  await authStore.check(true)
  loadFiltersFromSettings(authStore.settings)
  alertsStore.init(isSpot.value, isSpotSell.value)
  alertsTable.value?.setSort('spread', -1)
  alertsStore.page = 0
  pageInited.value = true
  startWs()
})

// Обработка формы фильтрации
const onFilterFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid || !pageInited.value) {
    return
  }
  const values = keysToSnake(e.values)
  if (typeof values.symbols === 'string') {
    values.symbols = parseCommaSeparatedAssets(values.symbols)
  }
  alertsStore.filters = {
    ...alertsStore.filters,
    ...values
  }

  // Костыль. Надо будет пофиксить
  if (alertsStore.filters.spread_min === 0) {
    alertsStore.filters.spread_min = null
  }
  if (alertsStore.filters.funding_min === 0) {
    alertsStore.filters.funding_min = null
  }

  const prefix = getFilterPrefix(marketType.value)
  try {
    const prom = Promise.all([
      saveSwapFilters(alertsStore.filters as Record<string, unknown>, prefix),
      await alertsTable.value?.onPage(null, null, null, false)
    ])
    await prom
  } catch (err) {
    console.error('Failed to save filters:', err)
  }
}

const isAdvanced = computed(() => "futures_advanced" in authStore.currentAccessList)
watch(() => authStore.currentAccessList, (v) => {
  if (!("futures" in v || "futures_advanced" in v || "futures_free" in v)) {
    themeStore.forbidden = true
    return
  }
})

</script>

<template>
  <Card class="md:mb-2" pt:body:class="p-0" pt:root:class="rounded-none md:rounded-xl">
    <template #content>
      <MarketSpreadAlertsModal v-if="isAdvanced" ref="marketSpreadAlertsModal"></MarketSpreadAlertsModal>
      <AlertsFilters :inited="pageInited" panel-class="px-2 border-none mb-0" :loading="alertsStore.loading"
        :form-submit="onFilterFormSubmit" :form-data="formData" :filters="filters" :zod="zodForm"
        :grid-classes="gridClasses" :complexFields='["excludeSelectedSymbols"]'>
        <template #top-left>
          <span class="pit success font-bold">{{ $t(titleKey) }}</span>
        </template>
        <template #actions>
          <Button size="small" v-if="isAdvanced" severity="secondary" rounded icon="pi pi-exclamation-triangle"
            @click="marketSpreadAlertsModal?.toggle()"></Button>
          <InputPopover size="small" icon="pi pi-telegram" severity="secondary" rounded>
            <template #content>
              <div class="grid grid-cols-1 gap-3">
                <!-- <IftaLabel>
                  <SrInputNumber :min="AMOUNT_MIN" :max="AMOUNT_MAX" :max-fraction-digits="0" :step="1"
                    @change="updateAmount" v-model="amountUsdt" fluid>
                  </SrInputNumber>
                  <label>{{ $t('usdtAmount') }}</label>
                </IftaLabel> -->
                <ToggleButton size="small" fluid v-model="tgSignals" :offLabel="$t('enableTgSignals')"
                  :onLabel="$t('disableTgSignals')" />
                <Button severity="info" target="_blank" as="a" :href="tgBotLink" icon="pi pi-telegram" size="small"
                  :label="$t('openTgSignalsBot')"></Button>
              </div>
            </template>
          </InputPopover>
        </template>
      </AlertsFilters>
      <Divider class="mt-0 mb-0"></Divider>
      <AlertsTable :paginator="true" :lazy="true" :formatting-rules="formattingRules" ref="alertsTable"
        :columns="tableColumns" :fake-rows-count="15" :store="alertsStore" :col-full="true"
        :groupSortField="alertsStore.sortBy" :groupSortOrder="alertsStore.sortOrder" :table-component="SrGroupDataTable"
        :table-component-props="{
          groupBy: 'symbol.id',
        }">
      </AlertsTable>
    </template>
  </Card>
</template>
